import { useState } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import { BRAND, EXAM_TYPE_LABEL, FITNESS_LABEL, STATION_LABEL } from '@dru/types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ExamStatus, ExaminationDetail as Shape, FitnessDecision as Decision, Venue } from '@dru/types';
import { APPOINTMENT_STATUS_LABEL, CONSENT_METHOD_LABEL } from '@dru/types';
import {
  Alert, Button, Card, DataTable, Detail, DetailHeader, DetailSection, Dialog, EmptyState, ErrorState, FitnessDecision, IconAction, InfoTip, RowActions, SearchInput, StatusBadge, Steps, TextField, TextSkeleton, useToast, type StepSpec, type Tone, MoreActions, PhoneFold,
} from '@dru/ui';
import { CalendarClock, Eye, Plus } from 'lucide-react';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { CancelExaminationDialog, RescheduleDialog } from '../components/AppointmentActions';
import { ArrangeDialog } from '../components/ArrangeDialog';
import { CaseDocuments } from '../components/CaseDocuments';
import { CompanyProfileReports } from '../components/CompanyProfileReports';
import { CandidateDecisionDialog } from '../components/CandidateDecisionDialog';
import { CASE_STEPS, caseStatusLabel, caseStatusTone, caseStep, nextCaseAction, NEXT_ACTION_LABEL } from './Examinations';

/** Until check-in the order is HR's; the API refuses additions after that. */
const BEFORE_CHECK_IN: ExamStatus[] = ['REQUESTED', 'ROUTED', 'CONSENT_PENDING', 'SCHEDULED', 'NO_SHOW_OPEN'];

/** "ROUTINE" → "Routine". For the handful of enum values with no shared label. */
const humanize = (v?: string | null) => (v ? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' ') : '—');

const APPT_TONE: Record<string, Tone> = {
  COMPLETED: 'covered', CHECKED_IN: 'covered', NO_SHOW: 'overdue', CANCELLED: 'neutral', RESCHEDULED: 'neutral',
};

const STATION_STATE: Record<string, { label: string; tone: Tone }> = {
  COMPLETED: { label: 'Done', tone: 'covered' },
  SKIPPED: { label: 'Skipped', tone: 'warning' },
  PENDING: { label: 'Not yet', tone: 'pending' },
  IN_PROGRESS: { label: 'In progress', tone: 'pending' },
};

/**
 * A test prescribed on top of the protocol. Added as non-mandatory, marked
 * extra, and billed as its own line — which is why a reason is required.
 */
function AddTestsDialog({ orderId, already, onClose, onDone }: {
  orderId: string; already: string[]; onClose: () => void; onDone: () => void;
}) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Array<{ id: string; code: string; name: string }>>([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tests = useQuery<{ items: Array<{ id: string; code: string; name: string; category: string }> }>(
    () => api.get(`/tests?brief=true&limit=50${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`), [q],
  );
  const exclude = new Set([...already, ...picked.map((t) => t.id)]);
  const candidates = (tests.data?.items ?? []).filter((t) => !exclude.has(t.id));

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/examinations/${orderId}/tests`, { testIds: picked.map((t) => t.id), reason: reason.trim() });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title="Add a test to this medical case" info="It joins the battery as optional and is invoiced as its own line at the rate card’s price for that test. For a test the protocol did not ask for — a doctor’s prescription, a company request." onClose={onClose}>
      <SearchInput value={q} onValueChange={setQ} placeholder="Test name or code" aria-label="Search tests" className="mt-3" />
      <ul className="mt-2 max-h-48 divide-y divide-neutral-100 overflow-y-auto rounded border border-neutral-200 text-sm">
        {tests.loading && <li className="px-3 py-2 text-xs text-neutral-500">Searching…</li>}
        {!tests.loading && candidates.length === 0 && <li className="px-3 py-2 text-xs text-neutral-500">No other test matches.</li>}
        {candidates.slice(0, 30).map((t) => (
          <li key={t.id} className="flex items-center justify-between gap-2 px-3 py-1">
            <span><span className="text-neutral-900">{t.name}</span> <span className="font-mono text-[11px] text-neutral-500">{t.code}</span></span>
            <IconAction label={`Add ${t.name}`} icon={Plus} onClick={() => setPicked((p) => [...p, t])} />
          </li>
        ))}
      </ul>
      {picked.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1" aria-label="Tests to add">
          {picked.map((t) => (
            <li key={t.id} className="flex items-center gap-1 rounded-badge bg-warning-100 py-0.5 pl-2 pr-0.5 text-caption text-warning-600">
              {t.name}
              <button type="button" onClick={() => setPicked((p) => p.filter((x) => x.id !== t.id))} aria-label={`Remove ${t.name}`}
                className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-warning-600/10">×</button>
            </li>
          ))}
        </ul>
      )}
      <TextField label="Why" value={reason} onChange={setReason} required className="mt-3"
        hint="At least five characters."
        info="For example, prescribed by Dr Rao after the pre-employment consult. Kept on the case for the auditor." />
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={picked.length === 0 || reason.trim().length < 5}>
          Add {picked.length || ''} {picked.length === 1 ? 'test' : 'tests'}
        </Button>
      </div>
    </Dialog>
  );
}

const OPEN: ExamStatus[] = [
  'REQUESTED', 'ROUTED', 'CONSENT_PENDING', 'SCHEDULED', 'NO_SHOW_OPEN', 'RETEST_REQUIRED',
];
const CLOSED: ExamStatus[] = ['CANCELLED', 'EXPIRED', 'NO_SHOW_CLOSED'];

const when = (v: string | null | undefined) => (v ? formatDateTime(v) : '—');

/**
 * The case's journey, as seven steps HR can read without knowing the state
 * machine: where it is, what is done, and what is stuck.
 */
function progress(o: Shape): StepSpec[] {
  // The same seven steps as the progress bar on the Medical cases list.
  const at = caseStep(o.status);
  const next = o.appointments?.find((a) => a.status === 'SCHEDULED' || a.status === 'CONFIRMED');
  // A signed decision that is not a certificate reads "Decision", not "Completed".
  const notCleared = o.outcome === 'UNFIT' || o.outcome === 'TEMPORARILY_UNFIT' || o.outcome === 'FURTHER_EVALUATION';
  return CASE_STEPS.map(({ key, label }, i) => {
    let state: StepSpec['state'] = at < 0 || i < at ? 'done' : i === at ? 'current' : 'upcoming';
    if (o.status === 'CERTIFIED') state = 'done';
    let detail: StepSpec['detail'];
    if (key === 'requested') {
      detail = o.status === 'REQUESTED' ? 'Waiting for a lab'
        : o.status === 'ROUTED' || o.status === 'CONSENT_PENDING' ? 'Lab assigned — book a slot'
          : formatDate(o.createdAt);
    } else if (key === 'scheduled' && o.status === 'NO_SHOW_OPEN') { state = 'blocked'; detail = 'Missed — to rebook'; }
    else if (key === 'scheduled' && o.status === 'SCHEDULED' && next) detail = formatDateTime(next.scheduledFor);
    else if (key === 'review' && o.status === 'RETEST_REQUIRED') { state = 'blocked'; detail = 'Retest requested'; }
    return { key, label: key === 'completed' && notCleared ? 'Decision' : label, state, detail };
  });
}

/** One sentence: what the case is waiting for, and whose move it is. */
function waitingFor(o: Shape, canRoute: boolean): string {
  switch (o.status) {
    case 'REQUESTED': return canRoute
      ? 'Assign a lab and book a slot.'
      : `Waiting for ${BRAND.name} Operations to assign a lab. You book the slot once they have.`;
    case 'ROUTED': return 'The lab is assigned. Book a slot for the employee.';
    case 'CONSENT_PENDING': return 'The lab is assigned and the employee’s consent is pending. Book a slot for the employee.';
    case 'SCHEDULED': return 'Booked. Nothing to do until the visit — the venue checks the employee in.';
    case 'NO_SHOW_OPEN': return 'The employee did not attend. Rebook them — the case stays open until they are examined.';
    case 'CHECKED_IN': case 'IN_PROGRESS': return 'The employee is at the venue being examined.';
    case 'LAB_PENDING': return 'Waiting for lab results. Nothing for you to do.';
    case 'REVIEW_PENDING': return 'With the physician for the fitness decision. Nothing for you to do.';
    case 'RETEST_REQUIRED': return 'The physician asked for a repeat examination before deciding. Raise the retest and book it.';
    case 'CERTIFIED': return o.outcome === 'FURTHER_EVALUATION'
      ? 'Further evaluation is required before clearance. Arrange follow-up with the clinical team.'
      : o.outcome === 'UNFIT'
      ? 'The physician has signed: unfit for duty. No certificate is issued for this outcome — the decision is below.'
      : o.outcome === 'TEMPORARILY_UNFIT'
        ? 'The physician has signed: temporarily unfit. No certificate is issued until the retest — the decision is below.'
        : 'Certified. The fitness decision is below.';
    default: return '';
  }
}

/**
 * One medical case: where it has reached and what it is waiting for, the
 * outcome once there is one, its appointments, and then the detail — the
 * tests it must run, consent, the visit's stations and the history.
 *
 * Nothing clinical: stations say done or not done, the certificate says the
 * decision and its validity. The health check report is shown only under the
 * company's own report policy, which the server enforces.
 */
export function ExaminationDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { toast } = useToast();
  const { permissions } = useAuth();
  const canRead = permissions.has('examination.read');
  // A 404 here is an answer, not a failure: the case does not exist, or it
  // belongs to a plant this login is not assigned to (the API does not say
  // which, on purpose). Kept apart from a real error, which is worth retrying.
  const [notFound, setNotFound] = useState(false);
  const order = useQuery<Shape | null>(() => {
    setNotFound(false);
    if (!canRead) return Promise.resolve(null);
    return api.get<Shape>(`/examinations/${id}`).catch((e) => {
      if (e instanceof ApiError && e.status === 404) { setNotFound(true); return null; }
      throw e;
    });
  }, [id, canRead]);
  const o = order.data;
  const [cancelling, setCancelling] = useState(false);
  const [moving, setMoving] = useState<{ a: Shape['appointments'][number]; mode: 'move' | 'rebook' } | null>(null);
  const [arranging, setArranging] = useState(false);
  const [decidingCandidate, setDecidingCandidate] = useState<'HIRE' | 'NOT_HIRED' | null>(null);
  const [raisingRetest, setRaisingRetest] = useState(false);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const [addingTests, setAddingTests] = useState(false);

  async function downloadCertificate(certificateId: string) {
    setDownloadingCert(true);
    try {
      const grant = await api.get<{ url: string }>(`/certificates/${certificateId}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast('danger', errorMessage(err));
    } finally {
      setDownloadingCert(false);
    }
  }

  async function handleRaiseRetest() {
    if (!o) return;
    setRaisingRetest(true);
    try {
      const next = await api.post<{ id: string }>(`/examinations/${o.id}/retest`, {}, crypto.randomUUID());
      toast('success', 'Retest raised as a new medical case. Book its slot next.');
      nav(`/hr/examinations/${next.id}`);
    } catch (err) {
      toast('danger', errorMessage(err));
      setRaisingRetest(false);
    }
  }

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Medical case" />
        <EmptyState title="Medical cases are not part of your role" hint="Ask your company administrator if you need to follow employees' medical cases." />
      </div>
    );
  }
  if (notFound) {
    return (
      <div>
        <PageHeader title="Medical case" />
        <EmptyState
          title="This medical case is not available to you"
          hint="It does not exist, or it belongs to a plant you are not assigned to. If you expected to see it, ask a company administrator to check your plant access."
          action={<Button variant="secondary" onClick={() => nav('/hr/examinations')}>Back to medical cases</Button>}
        />
      </div>
    );
  }
  if (order.error) {
    return (
      <div className="space-y-4">
        <ErrorState title="This medical case could not be opened" detail={order.error} onRetry={order.reload} />
      </div>
    );
  }

  // Cancelling is offered while the case is still HR's to act on — before
  // anybody has been examined. A checked-in worker's case is closed by the
  // venue, not withdrawn by the employer.
  const cancellable = o ? OPEN.includes(o.status as ExamStatus) : false;
  const snapshot = (o?.protocolSnapshot ?? null) as {
    jobRole?: { code: string; title: string } | null;
    hazards?: Array<{ code: string; name: string }>;
    configuration?: { name: string } | null;
    protocols?: Array<{ code: string; version: number; name: string }>;
    package?: { code: string; name: string; source: 'JOB_ROLE' | 'HR' | 'DEFAULT' | 'CAMP' } | null;
    tests?: Array<{ id: string; code: string; name: string; category: string; isMandatory: boolean; extra?: boolean; addedReason?: string }>;
  } | null;
  // Tests may be added until the worker is checked in; after that the
  // battery is the venue's. Same rule the API enforces — and the test
  // catalogue is its own permission, which a plant login may not hold.
  const canAddTests = o
    ? BEFORE_CHECK_IN.includes(o.status as ExamStatus) && holds(permissions, 'examination.create') && holds(permissions, 'test.read')
    : false;

  const action = o ? nextCaseAction(o.status, permissions) : null;
  const missed = o?.appointments?.find((a) => a.status === 'NO_SHOW');
  const closed = o ? CLOSED.includes(o.status as ExamStatus) : false;
  // From the certificate when there is one; from the signed review when the
  // decision was one that issues none. The candidate banner below needs it
  // most in exactly that case — an unfit candidate is the one HR must close.
  const decision = (o?.certificate?.decision ?? o?.outcome ?? undefined) as Decision | undefined;

  function runAction() {
    if (!o || !action) return;
    if (action === 'retest') void handleRaiseRetest();
    else if (action === 'rebook') { if (missed) setMoving({ a: missed, mode: 'rebook' }); }
    else setArranging(true);
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Medical case"
        title={o ? `${o.employee?.fullName ?? 'Employee'} · ${(EXAM_TYPE_LABEL[o.examinationType] ?? o.examinationType)}` : 'Loading…'}
        badges={o && <StatusBadge tone={caseStatusTone(o.status, o.outcome)}>{caseStatusLabel(o.status, o.outcome)}</StatusBadge>}
        subtitle={o && [o.employee?.employeeCode, `raised ${formatDate(o.createdAt)}`, o.dueDate ? `due ${formatDate(o.dueDate)}` : null].filter(Boolean).join(' · ')}
        actions={o && (
          <>
            {action && !(action === 'rebook' && !missed) && (
              <Button onClick={runAction} loading={action === 'retest' && raisingRetest}>{NEXT_ACTION_LABEL[action]}</Button>
            )}
            {cancellable && holds(permissions, 'examination.cancel') && (
              <MoreActions>
                <Button variant="secondary" className="text-danger-600" onClick={() => setCancelling(true)}>Cancel medical case</Button>
              </MoreActions>
            )}
          </>
        )}
      />

      {!o && <TextSkeleton lines={6} />}

      {o && (
        closed ? (
          <Alert tone="info" title={`This medical case is ${caseStatusLabel(o.status).toLowerCase()}`}>
            It is kept for the record. The employee is still owed their examination — arrange a new medical case from their page.
          </Alert>
        ) : (
          <Card className="p-4">
            {/* Folded on a phone; the "Now" line under it says where the case is. */}
            <PhoneFold label="case progress">
              <Steps steps={progress(o)} label="Case progress" />
            </PhoneFold>
            <p className="mt-4 border-t border-neutral-100 pt-3 text-body text-neutral-800">
              <span className="font-semibold text-neutral-900">Now: </span>
              {waitingFor(o, holds(permissions, 'examination.route'))}
            </p>
          </Card>
        )
      )}

      {o?.employee?.status === 'CANDIDATE' && decision && (
        <Alert
          tone={decision === 'FIT' ? 'success' : decision === 'UNFIT' ? 'danger' : 'warning'}
          title={`Pre-employment candidate — ${FITNESS_LABEL[decision] ?? decision}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {decision === 'FIT'
                ? 'The medical requirement is met. Mark the candidate hired to make them an active employee.'
                : decision === 'FIT_WITH_RESTRICTION'
                  ? 'Fit with restrictions. Check the plant can accommodate them before recording the hire.'
                  : 'The physician did not find the candidate fit for this role. Record your recruitment decision.'}
            </span>
            {holds(permissions, 'employee.update') && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setDecidingCandidate('HIRE')}>Mark hired</Button>
                <Button size="sm" variant="secondary" onClick={() => setDecidingCandidate('NOT_HIRED')}>Close candidate</Button>
              </div>
            )}
          </div>
        </Alert>
      )}

      {o?.encounter?.completionType === 'PARTIAL' && (
        <Alert tone="warning" title="Visit partly completed">
          The venue closed the visit before every mandatory station or test was done
          {o.encounter.partialReasonCode ? ` (${humanize(o.encounter.partialReasonCode).toLowerCase()})` : ''}.
          The physician decides whether a retest is needed.
        </Alert>
      )}

      {o?.certificate && decision && (
        <DetailSection
          title="Outcome"
          action={holds(permissions, 'certificate.download') ? (
            <Button
              size="sm"
              variant="secondary"
              loading={downloadingCert}
              disabled={o.certificate.generationStatus !== 'ISSUED'}
              title={o.certificate.generationStatus !== 'ISSUED' ? 'The PDF is still being generated' : undefined}
              onClick={() => downloadCertificate(o.certificate!.id)}
            >
              Download PDF
            </Button>
          ) : undefined}
        >
          <FitnessDecision
            decision={decision}
            detail={<>Valid {formatDate(o.certificate.validFrom)} to {formatDate(o.certificate.validUntil)}</>}
          />
          <p className="mt-2 text-small text-neutral-600">
            Certificate{' '}
            <Link to={`/hr/certificates/${o.certificate.id}`} className="font-mono font-medium text-primary-700 hover:underline">
              {o.certificate.certificateNumber}
            </Link>
            {' '}— restrictions and validity are on the certificate.
          </p>
        </DetailSection>
      )}

      {/* Signed, and no certificate followed: unfit, or temporarily unfit.
          The outcome and the retest date only — what the physician found,
          and why, is not HR's to read and is not sent to this page. */}
      {o && !o.certificate && (o.outcome === 'UNFIT' || o.outcome === 'TEMPORARILY_UNFIT' || o.outcome === 'FURTHER_EVALUATION') && (
        <DetailSection title="Outcome">
          <FitnessDecision
            decision={o.outcome}
            detail={(
              <>
                {o.decidedOn && <>Signed {formatDate(o.decidedOn)}. </>}
                {o.outcome === 'TEMPORARILY_UNFIT'
                  ? (o.retestDueOn ? `Retest due ${formatDate(o.retestDueOn)}.` : 'A retest is to be arranged.')
                  : o.outcome === 'FURTHER_EVALUATION' ? 'Further evaluation required before clearance.' : 'Not to be deployed in this role.'}
              </>
            )}
          />
          <p className="mt-2 text-small text-neutral-600">
            No fitness certificate is issued for this outcome, so the employee is not covered.{' '}
            {o.outcome === 'TEMPORARILY_UNFIT'
              ? 'Arrange a new medical case for the retest when it falls due.'
              : o.outcome === 'FURTHER_EVALUATION' ? 'Arrange further evaluation with the clinical team.' : 'Arrange a new medical case only if their role or their health changes.'}
          </p>
        </DetailSection>
      )}

      {o && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <DetailSection title="Appointments" className="min-w-0 lg:col-span-2">
            <DataTable<Shape['appointments'][number]>
              rows={o.appointments ?? []} rowKey={(a) => a.id}
              onRowClick={(a) => nav(`/hr/appointments/${a.id}`)}
              empty={{
                title: 'Not booked yet',
                hint: o.status === 'REQUESTED'
                  ? 'A slot is booked once the lab is assigned.'
                  : 'Book a slot with the button at the top of this page.',
              }}
              columns={[
                { key: 'when', header: 'Slot', cell: (a) => <span className="text-small tabular-nums text-neutral-900">{when(a.scheduledFor)}</span> },
                { key: 'venue', header: 'Venue', cell: (a) => {
                  const v = (a.venue ?? a.venueSnapshot) as Venue | null;
                  return (
                    <div className="text-small">
                      <div className="text-neutral-900">{v?.name ?? a.partner?.name ?? '—'}</div>
                      <div className="text-caption text-neutral-500">{[a.venueType === 'CAMP' ? 'Camp' : null, v?.city].filter(Boolean).join(' · ') || 'At the venue'}</div>
                    </div>
                  );
                } },
                { key: 'status', header: 'Status', mobileLabel: false, cell: (a) => <StatusBadge tone={APPT_TONE[a.status] ?? 'pending'}>{APPOINTMENT_STATUS_LABEL[a.status] ?? humanize(a.status)}</StatusBadge> },
                { key: 'checkin', header: 'Checked in', cell: (a) => <span className="text-small text-neutral-600">{when(a.checkedInAt)}</span> },
                {
                  key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
                  cell: (a) => (
                    <RowActions>
                      <IconAction label={`View appointment on ${when(a.scheduledFor)}`} icon={Eye} onClick={() => nav(`/hr/appointments/${a.id}`)} />
                      {a.status === 'NO_SHOW' && o.status === 'NO_SHOW_OPEN' && holds(permissions, 'appointment.reschedule') ? (
                        <IconAction label={`Rebook the missed appointment on ${when(a.scheduledFor)}`} icon={CalendarClock} primary onClick={() => setMoving({ a, mode: 'rebook' })} />
                      ) : (a.status === 'SCHEDULED' || a.status === 'CONFIRMED') && holds(permissions, 'appointment.reschedule') ? (
                        <IconAction label={`Move the appointment on ${when(a.scheduledFor)}`} icon={CalendarClock} onClick={() => setMoving({ a, mode: 'move' })} />
                      ) : null}
                    </RowActions>
                  ),
                },
              ]}
            />
          </DetailSection>

          <DetailSection title="Case details" phoneFolded className="min-w-0">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Detail label="Employee" value={o.employee ? <Link to={`/hr/employees/${o.employeeId}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">{o.employee.fullName}</Link> : '—'} />
              <Detail label="Employee ID" value={o.employee?.employeeCode ?? '—'} mono />
              <Detail label="Type" value={(EXAM_TYPE_LABEL[o.examinationType] ?? o.examinationType)} />
              <Detail label="Priority" value={humanize(o.priority)} />
              <Detail label="Due" value={o.dueDate ? formatDate(o.dueDate) : '—'} />
              <Detail label="Turnaround target" value={o.slaBreachedAt ? <span className="text-overdue-600">Missed on {when(o.slaBreachedAt)}</span> : o.slaDueAt ? `By ${when(o.slaDueAt)}` : '—'} />
              <Detail label="Campaign" value={o.campaignId ? <Link to={`/hr/campaigns/${o.campaignId}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">Part of a campaign</Link> : 'Raised individually'} />
            </dl>
          </DetailSection>

          <DetailSection
            title="Tests required"
            className="min-w-0"
            action={canAddTests ? (
              <Button size="sm" variant="ghost" onClick={() => setAddingTests(true)}>Add a test</Button>
            ) : undefined}
          >
            {snapshot ? (
              <dl className="space-y-3 text-small">
                {/* Job role, hazards and protocols only where the battery came
                    from them — a case raised before docs/38, or a worker who
                    still has a role recorded. Otherwise it is the company's
                    medical package. */}
                {snapshot.jobRole && (snapshot.protocols ?? []).length > 0 ? (
                  <>
                    <Detail label="Job role" value={snapshot.jobRole.title} />
                    <Detail label="Hazards" value={(snapshot.hazards ?? []).map((h) => h.name).join(', ') || '—'} />
                    <Detail label="Protocols" value={(snapshot.protocols ?? []).map((p) => `${p.name} v${p.version}`).join(', ') || '—'} />
                  </>
                ) : (
                  <Detail label="Tests from" value={snapshot.package?.name ?? snapshot.configuration?.name ?? 'Company medical package'} />
                )}
                <Detail label="Invoiced as" value={o.package
                  ? `${o.package.name} · ${o.packageSource === 'HR' ? 'chosen by HR' : String(o.packageSource) === 'DEFAULT' ? 'default for this examination' : String(o.packageSource) === 'CAMP' ? 'the camp\u2019s test plan' : 'company medical package'}`
                  : 'No package — priced by protocol or type'} />
                <div>
                  <dt className="flex items-center gap-1 text-caption font-medium text-neutral-600">
                    Test battery ({snapshot.tests?.length ?? 0})
                    <InfoTip label="About the test battery">
                      Fixed when the case was raised; a later catalogue change does not alter it.
                      A test marked extra was prescribed outside the protocol and is invoiced as its own line.
                    </InfoTip>
                  </dt>
                  <dd>
                    <ul className="mt-1 flex flex-wrap gap-1">
                      {(snapshot.tests ?? []).map((t) => (
                        <li key={t.id}
                          className={`rounded-badge px-1.5 py-0.5 text-micro ${t.extra ? 'bg-warning-100 text-warning-600' : t.isMandatory ? 'bg-primary-50 text-primary-700' : 'bg-neutral-100 text-neutral-600'}`}
                          title={t.extra ? `${t.code} — added: ${t.addedReason ?? ''}` : t.code}>
                          {t.name}{t.extra ? ' · extra' : ''}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
              </dl>
            ) : <p className="text-small text-neutral-500">No protocol snapshot recorded.</p>}
          </DetailSection>
          {addingTests && (
            <AddTestsDialog orderId={o.id} already={(snapshot?.tests ?? []).map((t) => t.id)}
              onClose={() => setAddingTests(false)}
              onDone={() => { setAddingTests(false); order.reload(); }} />
          )}

          <DetailSection title="Consent" phoneFolded className="min-w-0">
            {(o.consents ?? []).length === 0 ? (
              <p className="text-small text-neutral-500">Not recorded yet. The employee gives consent at booking or at the venue.</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {o.consents!.map((c) => (
                  <li key={c.id} className="py-2 text-small">
                    <div className="text-neutral-800">{c.purpose ?? 'Consent'}</div>
                    <div className="text-caption text-neutral-500">
                      {(c.method && CONSENT_METHOD_LABEL[c.method]) || humanize(c.method)} · {when(c.acceptedAt)}
                      {c.withdrawnAt && <span className="font-medium text-overdue-600"> · withdrawn {when(c.withdrawnAt)}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          <DetailSection
            title="Visit progress"
            phoneFolded
            className="min-w-0"
            action={o.encounter ? (
              <InfoTip label="About visit progress" className="mr-auto self-center">
                Stations say done or not done. What was found stays in the clinical record.
              </InfoTip>
            ) : undefined}
          >
            {!o.encounter ? <p className="text-small text-neutral-500">The employee has not checked in yet.</p> : (
              <>
                <ul className="divide-y divide-neutral-100">
                  {(o.encounter.stations ?? []).map((st) => {
                    const s = STATION_STATE[st.status] ?? { label: humanize(st.status), tone: 'pending' as Tone };
                    return (
                      <li key={st.id} className="flex items-center justify-between gap-2 py-2 text-small">
                        <span className="text-neutral-800">{STATION_LABEL[st.station] ?? humanize(st.station)}</span>
                        <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </DetailSection>

          {/* The lab's signed reports, one per test group. The HCR summary is
              the employee's own document (employee app), not HR's (31 Oct 2026). */}
          {holds(permissions, 'report.health-read') && (
            <DetailSection title="Health reports" className="min-w-0 lg:col-span-2">
              <CompanyProfileReports orderId={o.id} />
            </DetailSection>
          )}

          {holds(permissions, 'certificate.download') && (
            <DetailSection title="Statutory forms and documents" className="min-w-0 lg:col-span-2">
              <CaseDocuments orderId={o.id} />
            </DetailSection>
          )}

          <DetailSection title="History" className="min-w-0 lg:col-span-2">
            <ol className="divide-y divide-neutral-100">
              {(o.events ?? []).map((ev) => (
                <li key={ev.id} className="flex flex-wrap items-baseline gap-x-3 py-1.5 text-small">
                  <span className="w-44 shrink-0 tabular-nums text-neutral-500">{when(ev.occurredAt)}</span>
                  <span className="text-neutral-900">
                    {ev.fromStatus ? <>{caseStatusLabel(ev.fromStatus)} <span aria-hidden>→</span><span className="sr-only">to</span> </> : 'Raised as '}
                    {/* The closing step takes the outcome: a case signed unfit did not end "Certified". */}
                    {caseStatusLabel(ev.toStatus, ev.toStatus === 'CERTIFIED' ? o.outcome : undefined)}
                  </span>
                  {ev.reason && <span className="text-neutral-500">{ev.reason}</span>}
                </li>
              ))}
              {(o.events ?? []).length === 0 && <li className="py-2 text-small text-neutral-500">No changes recorded yet.</li>}
            </ol>
          </DetailSection>
        </div>
      )}

      {o && cancelling && (
        <CancelExaminationDialog
          orderId={o.id}
          who={o.employee?.fullName}
          onClose={() => setCancelling(false)}
          onDone={() => { setCancelling(false); order.reload(); }}
        />
      )}
      {o && arranging && (
        <ArrangeDialog
          order={o}
          onClose={() => { setArranging(false); order.reload(); }}
          onDone={() => { setArranging(false); order.reload(); }}
        />
      )}
      {o && moving && (
        <RescheduleDialog
          appointmentId={moving.a.id}
          partnerId={moving.a.partnerId}
          partnerName={moving.a.partner?.name}
          who={o.employee?.fullName}
          scheduledFor={moving.a.scheduledFor}
          mode={moving.mode}
          onClose={() => setMoving(null)}
          onDone={() => { setMoving(null); order.reload(); }}
        />
      )}
      {decidingCandidate && o?.employee && (
        <CandidateDecisionDialog
          employee={{ id: o.employeeId, fullName: o.employee.fullName, version: o.employee.version ?? 0 }}
          decision={decidingCandidate}
          onClose={() => setDecidingCandidate(null)}
          onDone={() => {
            setDecidingCandidate(null);
            order.reload();
            toast('success', decidingCandidate === 'HIRE' ? 'Candidate marked as hired.' : 'Candidate record closed.');
          }}
        />
      )}
    </div>
  );
}
